import React from 'react'

export interface RequiredContextProviderProps<T> {
  value: T
  children: React.ReactNode
}

/**
 * Creates a context with no default value, its provider, and a hook that throws when used outside
 * the provider (a missing provider is a wiring bug, never something to paper over with a default).
 *
 * The value passed to the provider must be memoized by the caller (`useShallowStableValue` /
 * `useStableCallbacks`): every consumer re-renders when its identity changes.
 */
export function createRequiredContext<T>(name: string) {
  const Context = React.createContext<T | null>(null)

  Context.displayName = name

  function Provider({ value, children }: RequiredContextProviderProps<T>) {
    return <Context.Provider value={value}>{children}</Context.Provider>
  }

  Provider.displayName = `${name}Provider`

  function useRequiredContext(): T {
    const value = React.useContext(Context)

    if (value === null) {
      throw new Error(`${name} is missing: render this component inside <${name}Provider>.`)
    }

    return value
  }

  return { Context, Provider, useRequiredContext }
}
